import React from 'react'

import ConfirmDeleteGroupModal from './ConfirmDeleteGroupModal'
import {
  PlanGroupsDocument,
  useDeletePlanGroupMutation,
  useDeleteTemplateGroupMutation,
  useGroupByIdQuery
} from '../../../../generated/graphql'
import useModal from '../../../../lib/hooks/useModal'
import { updateDeletePlanGroupCache } from '../../../../lib/cacheUtils'
import useNotification from '../../../../lib/hooks/useNotification'

type ConfirmDeleteGroupModalWrapperProps = {
  /** ID of Plan Group */
  id: string
  /** Determines visibility of modal */
  isOpen: boolean
  /** Indicates if this is a template */
  isTemplate: boolean
  /** ID of the plan/template */
  planId: string
}

const ConfirmDeleteGroupModalWrapper = ({
  id,
  isOpen,
  isTemplate,
  planId
}: ConfirmDeleteGroupModalWrapperProps) => {
  const query = useGroupByIdQuery({
    variables: { id },
    onError: () => {
      showErrorNotif('An error occurred. Please refresh the page.')
    }
  })
  const { showErrorNotif } = useNotification()
  const [deleteGroup] = useDeletePlanGroupMutation({
    variables: { input: id },
    update: (cache) => updateDeletePlanGroupCache(cache, id),
    onError: () => showErrorNotif('Plan group could not be deleted.', deleteGroup),
    optimisticResponse: {
      __typename: 'Mutation',
      deletePlanGroup: {
        __typename: 'ResponseStatus',
        id,
        message: '',
        success: true
      }
    }
  })
  const [deleteTemplateGroup] = useDeleteTemplateGroupMutation({
    variables: { input: id },
    update: (cache) => updateDeletePlanGroupCache(cache, id),
    refetchQueries: [
      // Because of the re-sequencing that happens when a group is deleted, refetch groups
      // so the titles are accurate.  The logic to re-sequence is complicated
      // enough that it should stay in the backend.
      {
        query: PlanGroupsDocument,
        variables: { planId }
      }
    ],
    onError: () => showErrorNotif('Template group could not be deleted.', deleteTemplateGroup),
    optimisticResponse: {
      __typename: 'Mutation',
      deleteTemplateGroup: {
        __typename: 'ResponseStatus',
        id,
        message: '',
        success: true
      }
    }
  })
  const { hideModal } = useModal()
  const onConfirm = () => {
    if (isTemplate) {
      deleteTemplateGroup()
    } else {
      deleteGroup()
    }
    hideModal()
  }

  if (query.loading) {
    return null
  }

  const isDisabled = !!query.data?.planGroup.items.length

  return <ConfirmDeleteGroupModal isDisabled={isDisabled} isOpen={isOpen} onConfirm={onConfirm} />
}

export default ConfirmDeleteGroupModalWrapper
